import { useEffect, useMemo, useRef, useState } from 'react';
import { digitsOnly, stripSpaces, formatCardNumber as formatCardNumberUtil } from '../../utils/formatting';

export default function useCardVisual({
                                          formatTRY,
                                          getTotalWithCommission,
                                          validation,
                                          selectedInstallment,
                                          cardVisual,
                                          setCardVisual
                                      }) {
    const cardRef = useRef(null);
    const [isCardFlipped, setIsCardFlipped] = useState(false);

    // Mouse move animation
    useEffect(() => {
        const el = cardRef.current;
        if (!el) return;
        const onMouseMove = (e) => {
            const rect = el.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            const rotateX = (+1) * (y / rect.height) * 10;
            const rotateY = (-1) * (x / rect.width) * 12;
            el.style.setProperty('--tiltX', `${rotateX}deg`);
            el.style.setProperty('--tiltY', `${rotateY}deg`);
        };
        const onMouseLeave = () => {
            el.style.setProperty('--tiltX', `0deg`);
            el.style.setProperty('--tiltY', `0deg`);
        };
        el.addEventListener('mousemove', onMouseMove);
        el.addEventListener('mouseleave', onMouseLeave);
        return () => {
            el.removeEventListener('mousemove', onMouseMove);
            el.removeEventListener('mouseleave', onMouseLeave);
        };
    }, []);

    // Update amount display
    useEffect(() => {
        const amt = parseFloat((validation.values.amount || '').replace(',', '.'));
        if (isNaN(amt) || amt <= 0) {
            setCardVisual(prev => ({ ...prev, amount: formatTRY(0) }));
            return;
        }
        const total = getTotalWithCommission(amt, selectedInstallment.commission);
        setCardVisual(prev => ({ ...prev, amount: formatTRY(total) }));
    }, [validation.values.amount, selectedInstallment.commission, formatTRY, getTotalWithCommission, setCardVisual]);

    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 15 }, (_, i) => currentYear + i);
    }, []);

    const handleOwnerChange = (e) => {
        const val = (e.target.value || '').toUpperCase();
        validation.setFieldValue('cc_owner', val);
        setCardVisual(prev => ({ ...prev, owner: val || 'AD SOYAD' }));
    };

    const handleCardNumberChange = (e, onBinCandidate) => {
        const formatted = formatCardNumberUtil(e.target.value);
        validation.setFieldValue('card_number', formatted);
        const val = formatted.replace(/\s/g, '');
        let displayVal = '';
        if (val.length > 0) {
            if (val.length <= 4) displayVal = val;
            else if (val.length >= 8) displayVal = val.substr(0, 4) + ' •••• •••• ' + val.substr(val.length - 4, 4);
            else displayVal = val.substr(0, 4) + ' ' + '•'.repeat(val.length - 4);
        } else {
            displayVal = '•••• •••• •••• ••••';
        }
        setCardVisual(prev => ({ ...prev, number: displayVal }));

        if (val.length >= 8) onBinCandidate(val.substring(0, 8));
        else onBinCandidate(null);
    };

    const handleMonthChange = (e) => {
        validation.setFieldValue('expiry_month', e.target.value);
        setCardVisual(prev => ({ ...prev, month: e.target.value || 'AA' }));
    };

    const handleYearChange = (e) => {
        validation.setFieldValue('expiry_year', e.target.value);
        setCardVisual(prev => ({ ...prev, year: e.target.value || 'YY' }));
    };

    const handleCVVChange = (e) => {
        validation.setFieldValue('cvv', e.target.value);
        setCardVisual(prev => ({ ...prev, cvv: e.target.value ? e.target.value.replace(/./g, '•') : '•••' }));
    };

    const isCardValid = useMemo(() => {
        const number = stripSpaces(validation.values.card_number || '');
        const cvv = digitsOnly(validation.values.cvv || '');
        const amt = Number(validation.values.amount);
        return (
            !!validation.values.cc_owner &&
            number.length >= 13 &&
            !!validation.values.expiry_month &&
            !!validation.values.expiry_year &&
            cvv.length >= 3 &&
            !isNaN(amt) && amt >= 1
        );
    }, [validation.values]);

    const getCardBackground = () => {
        const schema = cardVisual.schema;
        if (schema === 'VISA') return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)';
        if (schema === 'MASTERCARD') return 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 50%, #ffb74d 100%)';
        if (schema === 'TROY') return 'linear-gradient(135deg, #e1f5fe 0%, #81d4fa 50%, #4fc3f7 100%)';
        if (schema === 'AMEX') return 'linear-gradient(135deg, #f3e5f5 0%, #ce93d8 50%, #ba68c8 100%)';
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)';
    };

    return {
        cardRef,
        cardVisual,
        setCardVisual,
        isCardFlipped,
        setIsCardFlipped,
        handleOwnerChange,
        handleCardNumberChange,
        handleMonthChange,
        handleYearChange,
        handleCVVChange,
        isCardValid,
        years,
        getCardBackground
    };
}